import REACT, { useEffect, useRef, useState } from 'react';
import { FaArrowRightLong } from "react-icons/fa6";
import HeroLeftImage from '@assets/LandingPage/HeroLeft.png';
import HeroMiddleImage from '@assets/LandingPage/HeroMiddle.png';
import './HeroSection.css';

type SparklineMeta = {
  path: string;
  lastPoint: { x: number; y: number };
};

const buildSparkline = (
  values: number[],
  width = 200,
  height = 60
): SparklineMeta => {
  if (!values.length) {
    return { path: '', lastPoint: { x: 0, y: 0 } };
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const step = width / Math.max(values.length - 1, 1);

  const points = values.map((val, index) => {
    const x = index * step;
    const normalized = (val - min) / range;
    const y = height - normalized * height;
    return { x, y };
  });

  const controlPoint = (
    current: { x: number; y: number },
    previous: { x: number; y: number },
    next: { x: number; y: number },
    reverse = false
  ) => {
    const smooth = 0.25;
    const p = previous || current;
    const n = next || current;
    const angle = Math.atan2(n.y - p.y, n.x - p.x) + (reverse ? Math.PI : 0);
    const length = Math.sqrt((n.x - p.x) ** 2 + (n.y - p.y) ** 2) * smooth;
    return {
      x: current.x + Math.cos(angle) * length,
      y: current.y + Math.sin(angle) * length
    };
  };

  const bezierPath = points.reduce((acc, point, idx, arr) => {
    if (idx === 0) {
      return `M${point.x},${point.y}`;
    }
    const prev = arr[idx - 1];
    const next = arr[idx + 1];
    const cp1 = controlPoint(prev, arr[idx - 2], point);
    const cp2 = controlPoint(point, prev, next, true);
    return `${acc} C${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${point.x},${point.y}`;
  }, '');

  return {
    path: bezierPath,
    lastPoint: points[points.length - 1]
  };
};

const expenseBreakdown = {
  title: 'Low Training Costs',
  filterLabel: 'Lowest Expense',
  segments: [
    { label: 'Training', value: '€ 1,140', color: '#C6C1FF' },
    { label: 'Other', value: '€ 12,400', color: '#E0DBA2' }
  ]
};

const learningProgress = {
  title: 'Faster Learning Curve',
  growth: '+200%',
  timeline: ['Day 1', 'Day 2', 'Day 3'],
  data: [15, 18, 14, 22, 20, 28, 26]
};

const AssaHero = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const metricsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!metricsRef.current || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            if (metricsRef.current) {
              observer.unobserve(metricsRef.current);
            }
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -15% 0px'
      }
    );

    observer.observe(metricsRef.current);

    return () => {
      if (metricsRef.current) {
        observer.unobserve(metricsRef.current);
      }
    };
  }, [hasAnimated]);

  const sparkline = buildSparkline(learningProgress.data);

  return (
    <section className="assa-hero">
      <div className="assa-hero__container">

        {/* LEFT – TEXT & CTA */}
        <div className="assa-hero__content">
          <h1 className="assa-hero__heading">
            Transform Your <br /> Team's Learning <br /> Journey
          </h1>

          <p className="assa-hero__subheading">
            Accelerate team productivity with comprehensive <br />
            skill development. Join 500+ enterprise clients <br />
            who've transformed their workforce.
          </p>

          <div className="assa-hero__cta">
            <button className="assa-hero__btn assa-hero__btn--primary">
              Get Started
            </button>
            <button className="assa-hero__btn assa-hero__btn--secondary">
              <span className="assa-hero__btn-text">Watch Demo for free</span>
              <FaArrowRightLong size={20} className="assa-hero__btn-arrow" />
            </button>
          </div>
        </div>

        {/* RIGHT – PRESERVED DESKTOP LAYOUT (SCALED ON MOBILE) */}
        <div className="assa-hero__boxes-wrapper">
          <div className="assa-hero__boxes-desktop-layout">
            {/* Left Box */}
            <div className="assa-hero__box assa-hero__box--left">
              <img 
                src={HeroLeftImage} 
                alt="Hero Left" 
                className="assa-hero__box-image"
              />
            </div>

            {/* Middle Box */}
            <div className="assa-hero__box assa-hero__box--middle">
        
            </div>

            {/* Right Side - Stacked Cards */}
            <div className="assa-hero__right-wrapper" ref={metricsRef}>
              {/* Background large box */}
              <div className="assa-hero__box assa-hero__box--right"></div>

              {/* Upper Insight Card */}
              <div className="assa-hero__box assa-hero__box--right-upper">
                <div className="assa-hero__insight-card">
                  <div className="assa-hero__insight-header">
                    <div>
                      <p className="assa-hero__insight-title">{expenseBreakdown.title}</p>
                    </div>
                    <button className="assa-hero__insight-filter">
                      {expenseBreakdown.filterLabel}
                    </button>
                  </div>
                  <div className="assa-hero__insight-body">
                    <div className="assa-hero__donut-chart">
                      <div className="assa-hero__donut-inner"></div>
                    </div>
                    <div className="assa-hero__legend">
                      {expenseBreakdown.segments.map((segment) => (
                        <div key={segment.label} className="assa-hero__legend-row">
                          <span
                            className="assa-hero__legend-dot"
                            style={{ backgroundColor: segment.color }}
                          ></span>
                          <span className="assa-hero__legend-label">{segment.label}</span>
                          <span className="assa-hero__legend-value">{segment.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Lower Progress Card */}
              <div className="assa-hero__box assa-hero__box--right-lower">
                <div className="assa-hero__progress-card">
                  <div className="assa-hero__progress-header">
                    <p className="assa-hero__progress-title">{learningProgress.title}</p>
                    <span className="assa-hero__progress-pill">{learningProgress.growth}</span>
                  </div>
                  <div className="assa-hero__progress-graph">
                    <svg
                      viewBox="0 0 200 60"
                      preserveAspectRatio="none"
                      role="img"
                      aria-label="Learning curve sparkline"
                    >
                      <path
                        d={sparkline.path}
                        className={
                          hasAnimated
                            ? 'assa-hero__progress-line assa-hero__progress-line--animated'
                            : 'assa-hero__progress-line'
                        }
                        pathLength={1}
                      />
                      <circle
                        cx={sparkline.lastPoint.x}
                        cy={sparkline.lastPoint.y}
                        r={4}
                        className={
                          hasAnimated
                            ? 'assa-hero__progress-marker-circle assa-hero__progress-marker-circle--animated'
                            : 'assa-hero__progress-marker-circle'
                        }
                      />
                    </svg>
                  </div>
                  <div className="assa-hero__progress-timeline">
                    {learningProgress.timeline.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AssaHero;